import React, {memo, useEffect, useState} from 'react';
import {renderToStaticMarkup} from 'react-dom/server';
import {useTranslation} from 'react-i18next';
import relativeTime from 'dayjs/plugin/relativeTime';
import InfiniteScroll from 'react-infinite-scroller';
import config from '../../service/config';
import {MediaCard, Spinner, EmptyState, TscMediaOrganizationType} from '@techsmith/tsc-cloud-style-guide';
import feedApi from '../../service/feedApi';
import {IFeed} from '../../model/feed';
import EmptyFeed from '../../static/svg/EmptyFeed.svg';
import {IFeedMediaSummary} from '../../model/mediaDetails';
import dayjs from 'dayjs';
import {GridLayoutContainer, LibraryContentContainer, SubheaderTitleContainer, Subheader, SubheaderControls, SubheaderTitle} from '../util/StyledElements';
import decode from 'entity-decode';
import {useTscTheme} from '../../context/themeProvider';
import styled from 'styled-components';
import {clearActiveCollection} from '../../context/collectionsProvider';
import FeedGlyph24x24 from '../../static/svg/FeedGlyph24x24.svg';

dayjs.extend(relativeTime);

export const PrivateCollectionEmptyStateContainer = styled.div`
   display: flex;
   justify-content: center;
   align-items: center;
   min-height: 100%;
`;

export const RecentContentBase: React.FC = () => {
   const {t} = useTranslation();
   const theme = useTscTheme();
   const [feed, setFeed] = useState<IFeed>(null);
   const [feedCanLoadMore, setFeedCanLoadMore] = useState(true);

   useEffect(() => {
      clearActiveCollection();
   }, []);

   const getFeed = async (existingMedia?: IFeedMediaSummary[]) => {
      const result = await feedApi.getFeed(config.user?.TechSmithId, feed?.Cursor);

      // maintain one list of media
      result.Media = (existingMedia ?? []).concat(result.Media);

      // we can load more if the Cursor was set in the last response
      setFeedCanLoadMore(!!result?.Cursor);

      setFeed(result);
   };

   const svgDataUri = `data:image/svg+xml;base64,${btoa(renderToStaticMarkup(<EmptyFeed />))}`;
   const feedHasMedia = feed && feed.Media && feed.Media.length > 0;

   return (
      <InfiniteScroll
         loadMore={() => getFeed(feed?.Media)}
         hasMore={feedCanLoadMore}
         loader={(
            <Spinner
               key="spinner"
               themeName={theme}
            />
         )}
         useWindow={true}
      >
         <Subheader
            theme={theme}
            isVisible={true}
         >
            <SubheaderControls theme={theme}>
               <SubheaderTitleContainer theme={theme}>
                  <FeedGlyph24x24 />
                  <SubheaderTitle className="feed-recent">
                     {t('feed.recentContent')}
                  </SubheaderTitle>
               </SubheaderTitleContainer>
            </SubheaderControls>
         </Subheader>
         <LibraryContentContainer>
            <GridLayoutContainer hasMedia={feedHasMedia}>
               {feedHasMedia && feed.Media.map(media => (
                  <MediaCard
                     key={media.Hash}
                     folderTitle={media.CollectionTitle}
                     folderUrl={media.CollectionUrl}
                     mediaDate={dayjs(media.CollectionShareDateTime).fromNow()}
                     mediaDateTitle={dayjs(media.CollectionShareDateTime).toLocaleString()}
                     mediaDuration={media.Duration}
                     mediaUrl={`/${media.Hash}`}
                     ownerAvatarUrl={`${config.environmentData.ProfileApiUrl}/api/v1/profiles/${media.OriginatorId}/default/picture`}
                     ownerId={media.OriginatorId}
                     ownerName={media.Author}
                     themeName={theme}
                     thumbnailUrl={media.ThumbnailUrl}
                     title={decode(media.Title)}
                     maxTitleLines={1}
                     viewCount={media.ViewCount}
                     organizationType={TscMediaOrganizationType.collection}
                     hasCaptions={media.HasPublishedCaptions}
                  />
               ))}
               {feed && !feedHasMedia && (
                  <EmptyState
                     title={t(config.featureSwitches.IsScreencast ? 'feed.emptyTitleScreencast' : 'feed.emptyTitle')}
                     text={t(config.featureSwitches.IsScreencast ? 'feed.emptyTextScreencast' : 'feed.emptyText')}
                     imageSource={svgDataUri}
                     themeName={theme}
                     alt={t('feed.emptyImageAltText')}
                  />
               )}
            </GridLayoutContainer>
         </LibraryContentContainer>
      </InfiniteScroll>
   );
};

export default memo(RecentContentBase);
